import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CheckInModal = ({ onCheckInComplete }) => {
  const { getAuthHeaders, isSuperAdmin } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/attendance/my-status`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch attendance status', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // If already checked in, notify parent immediately
  useEffect(() => {
    if (status?.has_checked_in) {
      onCheckInComplete();
    }
  }, [status, onCheckInComplete]);

  const handleCheckIn = async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API}/api/attendance/check-in`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        toast.success('Checked in successfully');
        onCheckInComplete();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Check-in failed');
      }
    } catch (err) {
      toast.error('Network error during check-in');
    } finally {
      setChecking(false);
    }
  };

  // Don't show for super admins
  if (isSuperAdmin()) return null;

  // Still loading
  if (loading) return null;

  // Already checked in today
  if (status?.has_checked_in) return null;

  const timeStatusType = status?.time_status_type || 'early';
  const timeStatusText = status?.time_status || '';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="checkin-modal-overlay"
    >
      <div
        className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8 flex flex-col items-center gap-5 animate-in fade-in zoom-in-95 duration-300"
        data-testid="checkin-modal"
      >
        {/* Clock icon */}
        <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
          <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>

        {/* Title */}
        <h2
          className="text-xl font-semibold text-zinc-900 dark:text-zinc-100"
          data-testid="checkin-modal-title"
        >
          Check In Required
        </h2>

        {/* Time status badge */}
        {timeStatusText && (
          <span
            className={`text-sm font-medium px-3 py-1 rounded-full ${
              timeStatusType === 'early'
                ? 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/40'
                : timeStatusType === 'late'
                ? 'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-950/40'
                : 'text-blue-700 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/40'
            }`}
            data-testid="checkin-time-status"
          >
            {timeStatusText}
          </span>
        )}

        {/* Description */}
        <p
          className="text-sm text-zinc-500 dark:text-zinc-400 text-center leading-relaxed"
          data-testid="checkin-modal-description"
        >
          Your daily check-in is how your line manager and HR manage your
          attendance. Please check in to continue using the system.
        </p>

        {/* Check In button */}
        <button
          onClick={handleCheckIn}
          disabled={checking}
          className="w-full mt-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium py-3 px-6 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="checkin-now-button"
        >
          {checking ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking in...
            </>
          ) : (
            'Check In Now'
          )}
        </button>
      </div>
    </div>
  );
};

export default CheckInModal;
